import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';

import css from './SectionHowVivianWorksVideo.module.css';

import Icons from '../../../components/Icons/Icons';
import videoSrc from '../../../assets/whatIsVivianlab.mp4';
import shapeTopLeft from '../../../assets/landing/how-vivian-works-shape-1.svg';
import shapeBottomLeft from '../../../assets/landing/how-vivian-works-shape-2.svg';
import shapeBottomRight from '../../../assets/landing/how-vivian-works-shape-3.svg';

const VIDEO_CONTROLS_SAFE_ZONE = 56;

export const SectionHowVivianWorksVideoComponent = props => {
  const { className, intl } = props;
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const playVideo = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.paused) return;

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  }, []);

  const handleVideoClick = useCallback(
    event => {
      const video = videoRef.current;
      if (!video || !video.paused) return;

      const rect = video.getBoundingClientRect();
      const isControlsArea = event.clientY >= rect.bottom - VIDEO_CONTROLS_SAFE_ZONE;
      if (isControlsArea) return;

      playVideo();
    },
    [playVideo]
  );

  const handleOverlayClick = useCallback(
    event => {
      event.preventDefault();
      event.stopPropagation();
      playVideo();
    },
    [playVideo]
  );

  return (
    <section className={classNames(css.root, className)}>
      <span className={classNames(css.decorativeShape, css.shapeTopLeft)} aria-hidden="true">
        <img src={shapeTopLeft} alt="" draggable="false" />
      </span>
      <span className={classNames(css.decorativeShape, css.shapeBottomLeft)} aria-hidden="true">
        <img src={shapeBottomLeft} alt="" draggable="false" />
      </span>
      <span className={classNames(css.decorativeShape, css.shapeBottomRight)} aria-hidden="true">
        <img src={shapeBottomRight} alt="" draggable="false" />
      </span>

      <div className={css.inner}>
        <header className={css.header}>
          <h2 className={css.title}>
            <FormattedMessage
              id="SectionHowVivianWorksVideo.title"
              values={{ brand: chunks => <span>{chunks}</span> }}
            />
          </h2>
          <p className={css.subtitle}>
            {intl.formatMessage({ id: 'SectionHowVivianWorksVideo.subtitle' })}
          </p>
        </header>

        <div className={css.videoFrame}>
          <video
            ref={videoRef}
            className={classNames(css.video, { [css.videoPaused]: !isPlaying })}
            controls
            playsInline
            preload="metadata"
            onClick={handleVideoClick}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
          >
            <source src={videoSrc} type="video/mp4" />
            {intl.formatMessage({ id: 'SectionHowVivianWorksVideo.videoFallback' })}
          </video>

          <button
            className={classNames(css.playOverlay, { [css.playOverlayHidden]: isPlaying })}
            type="button"
            onClick={handleOverlayClick}
            aria-label={intl.formatMessage({ id: 'SectionHowVivianWorksVideo.playLabel' })}
            aria-hidden={isPlaying}
            tabIndex={isPlaying ? -1 : undefined}
          >
            <span className={css.playIcon}>
              <Icons name="play" />
            </span>
          </button>
        </div>
      </div>
    </section>
  );
};

SectionHowVivianWorksVideoComponent.defaultProps = {
  className: null,
};

SectionHowVivianWorksVideoComponent.propTypes = {
  className: PropTypes.string,
  intl: intlShape.isRequired,
};

export default injectIntl(SectionHowVivianWorksVideoComponent);
